import {
    BatchUpdateProperty,
    Team,
    TeamDialogService,
    TeamMember,
    TeamMemberDialogService,
    TeamMemberService,
    TeamService
} from '@teammood/team';
import { sortBy } from 'lodash';
import { filter, from, mergeMap, Subject, takeUntil, tap } from 'rxjs';
import { Filter, TeamMembersFilterService } from './team-members-filter.service';
import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute } from "@angular/router";

type SelectableTeamMember = TeamMember & { checked: boolean };

@Component({
    selector: 'team-members-setup',
    templateUrl: './team-members-setup.component.html',
    providers: [
        TeamMembersFilterService,
        MatTooltipModule
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TeamMembersSetupComponent implements OnInit, OnDestroy {

    protected team: Team;

    allTeamMembers: SelectableTeamMember[] = [];
    selectedTeamMembers: SelectableTeamMember[] = [];
    filters: Filter[];
    teammembers: SelectableTeamMember[];
    search_terms: string;
    select_all_state: boolean;
    teamId: string;

    private _destroy$ = new Subject<void>();

    constructor(
        private teamMemberDialogService: TeamMemberDialogService,
        private teamMemberService: TeamMemberService,
        private teamService: TeamService,
        private teamMembersFilterService: TeamMembersFilterService,
        private teamDialogService: TeamDialogService,
        private route: ActivatedRoute,
    ) {
        
    }

    async ngOnInit() {

        this.route.params.pipe(
            filter(params => !!params['teamId']),
            mergeMap(params => {
                this.teamId = params['teamId'];
                return this.teamService.get(this.teamId);
            }),
            mergeMap(team => {
                this.team = team;
                return from(this.loadTeamMembers());
            }),
            takeUntil(this._destroy$)
        ).subscribe();

    }

    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }
    
    loadTeamMembers(teamMemberChecked = []) {

        if (this.team && this.team.id) {
            return this.teamMemberService.query(this.team.id).then((teamMembers) => {
                const selectableTeamMembers = teamMembers.map((teamMember) => {
                    const isTeamMemberChecked = teamMemberChecked.some(member => member.id === teamMember.id);
                    return {
                        ...teamMember,
                        checked: isTeamMemberChecked
                    };
                });

                this.allTeamMembers = sortBy(selectableTeamMembers, ['lastname']);

                //Update the selected team members
                this.selectedTeamMembers = this.selected_team_members();

                if (this.filters) {
                    this.applyFilters();
                } else {
                    this.teammembers = this.allTeamMembers;
                    this.filters = this.teamMembersFilterService.getFilters(this.allTeamMembers);
                }

                this.updateSelectAllState();
            });
        }
    }

    private selected_team_members(): SelectableTeamMember[] {
        return this.allTeamMembers.filter((member: SelectableTeamMember) => {
            return member.checked;
        });
    }

    onChecked() {
        this.selectedTeamMembers = this.selected_team_members();
        this.updateSelectAllState();
    }

    updateSelectAllState() {
        this.select_all_state = this.teammembers.length > 0 && !this.teammembers.some(teamMember => !teamMember.checked);
    }

    reloadTeamMembers(){
        const selectedTeamMembers = this.selected_team_members();
        this.loadTeamMembers(selectedTeamMembers);
    }

    async createTeamMember() {
        const dialogRef = await this.teamMemberDialogService.create(this.team);

        dialogRef.beforeClosed().pipe(
            filter((shouldReload) => !!shouldReload),
            tap(() => this.reloadTeamMembers()),
            takeUntil(dialogRef.afterClosed())
        ).subscribe();
    }

    select_all(){
        if (this.teammembers) {
            this.teammembers = this.teammembers.map((member: SelectableTeamMember) => {
                member.checked = !this.select_all_state;
                return member;
            });
            this.selectedTeamMembers = this.selected_team_members();
            this.updateSelectAllState();
        }
    }

    async openBatchUpdateDialog(updateProperty: BatchUpdateProperty, dialogWidth: string = '500px') {
        const dialogRef = await this.teamMemberDialogService.batchUpdate(this.team, this.selected_team_members(), updateProperty, dialogWidth);

        dialogRef.beforeClosed().pipe(
            filter((shouldReload) => !!shouldReload),
            tap(() => this.reloadTeamMembers()),
            takeUntil(dialogRef.afterClosed())
        ).subscribe()
    }

    toggleFilter(filterId: string) {
        this.filters = this.teamMembersFilterService.toggleFilter(this.filters, filterId);
        this.applyFilters();
    }

    applyFilters() {
        this.teammembers = this.teamMembersFilterService.applyFilters(this.filters, this.allTeamMembers, this.search_terms);
        this.filters = this.teamMembersFilterService.refreshFilters(this.filters, this.teammembers, this.allTeamMembers.length);
        this.updateSelectAllState();
    }

    clear_search() {
        this.search_terms = "";
        this.applyFilters();
    }

    openDefaultSettingsDialog = async () => {
        if(this.team) {
            const dialogRef = await this.teamDialogService.teamDefaults(this.team);

            dialogRef.beforeClosed().pipe(
                filter((updatedTeam) => !!updatedTeam),
                tap((updatedTeam) => {
                    this.team = updatedTeam;
                }),
                takeUntil(dialogRef.afterClosed())
            ).subscribe()
        }
    }

    openBatchImportDialog = async () => {
        if(this.team) {
            await this.teamDialogService.importTeamMembers(this.team);
        }
    }

    openBatchExportDialog = async () => {
        if(this.team) {
            await this.teamDialogService.exportTeamMembers(this.team);
        }
    }
}